import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Typography,
  Box,
  Button,
  IconButton,
  Card,
  Chip,
  Divider,
  CircularProgress,
  Grid2 as Grid,
  Tooltip,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
} from '@mui/material';
import { formatCurrency, formatDuration } from '../utils/formatters';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Badge as BadgeIcon,
  CameraAlt as CameraIcon,
  Schedule as ScheduleIcon,
  Search as SearchIcon,
  Share as ShareIcon,
  Payment as PaymentIconMui,
  Close as CloseIcon,
} from '@mui/icons-material';
import ProfessionalAvatar from '../components/ProfessionalAvatar';
import photoService from '../services/photoService';
import appointmentsService from '../services/appointmentsService';
import { useThemeCustom } from '../hooks/useThemeCustom';
import { useContextualHint } from '../hooks/useContextualHint';
import useDialog from '../hooks/useDialog';
import useSnackbar from '../hooks/useSnackbar';
import { useProfessionals } from '../hooks/queries';
import { useQueryClient } from '@tanstack/react-query';
import LoadingState from '../components/ui/LoadingState';
import EmptyState from '../components/ui/EmptyState';
import ErrorState from '../components/ui/ErrorState';
import CascadeDeleteDialog from '../components/ui/CascadeDeleteDialog';
import PageLayout from '../components/ui/PageLayout';
import PageFilters from '../components/ui/PageFilters';
import ProfessionalForm from '../components/forms/ProfessionalForm';
import ProfilePhotoUpload from '../components/ProfilePhotoUpload';
import ShareProfessionalDialog from '../components/ShareProfessionalDialog';
import ProfessionalCustomPricesDialog from '../components/professionals/ProfessionalCustomPricesDialog';
import AppointmentFormDialog from '../components/AppointmentFormDialog';
import AppSnackbar from '../components/ui/AppSnackbar';
import { getInitials } from '../utils/professionalNameUtils';
import { ProfessionalsDataProvider, useProfessionalsData } from '../contexts/ProfessionalsDataContext';
import { useLocation } from 'react-router-dom';
import SmartWizard from '../components/wizard/SmartWizard';
import { getIconComponent, getIconClass } from '../utils/iconUtils';

export default function ProfessionalsPage() {
  const location = useLocation();
  const [photoTimestampOverrides, setPhotoTimestampOverrides] = useState({});
  const { items: rawData, loading, error, createAsync, updateAsync, reload } = useProfessionals();
  const formDialog = useDialog();
  const deleteDialog = useDialog();
  const photoDialog = useDialog();
  const shareDialog = useDialog();
  const customPricesDialog = useDialog();
  const { snackbar, showSuccess, showError, hideSnackbar} = useSnackbar();
  const [showSmartWizard, setShowSmartWizard] = useState(false);

  // ✅ Estado para modal de agendamento
  const [appointmentDialogOpen, setAppointmentDialogOpen] = useState(false);
  const [appointmentPrefillConfig, setAppointmentPrefillConfig] = useState(null);
  const [appointmentNextSlots, setAppointmentNextSlots] = useState([]);
  const [loadingNextSlots, setLoadingNextSlots] = useState({});
  
  const normalizedData = useMemo(() => {
    if (!rawData) return null;
    return {
      ...rawData,
      professionals: (rawData.professionals || []).map(p => ({
        ...p,
        photoTimestamp: photoTimestampOverrides[p.id] ?? photoService.getPhotoTimestamp(p.id),
      })),
    };
  }, [rawData, photoTimestampOverrides]);

  const { getIcon, getLabel } = useThemeCustom();
  
  // Estado para foto
  const [selectedProfessional, setSelectedProfessional] = useState(null);

  // Cache de timestamps de fotos (centralizado no photoService)

  // Obter dica contextual
  const professionals = normalizedData?.professionals || [];
  const hint = useContextualHint('professionals', { items: professionals });

  // Abrir criação via querystring (?create=1)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('create') === '1') {
      setShowSmartWizard(true);
      const cleanUrl = location.pathname;
      window.history.replaceState(null, '', cleanUrl);
    }
  }, [location.pathname, location.search]);

  // ✅ Handlers CRUD
  const handleCreate = async (data) => {
    try {
      await createAsync(data);
      formDialog.handleClose();
      showSuccess('Profissional criado com sucesso!');
    } catch (err) {
      showError('Erro ao criar: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleUpdate = async (data) => {
    try {
      await updateAsync(formDialog.data.id, data);
      formDialog.handleClose();
      showSuccess('Profissional atualizado com sucesso!');
    } catch (err) {
      showError('Erro ao atualizar: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleSubmit = formDialog.isEdit ? handleUpdate : handleCreate;

  // ✅ Handlers de Agendamento
  const handleOpenAppointmentDialog = (professionalId, serviceId = null, placeId = null, nextSlots = []) => {
    setAppointmentPrefillConfig({
      professionalId,
      serviceId,
      placeId,
      nextSlots
    });
    setAppointmentNextSlots(nextSlots);
    setAppointmentDialogOpen(true);
  };

  const handleCloseAppointmentDialog = () => {
    setAppointmentDialogOpen(false);
    setAppointmentPrefillConfig(null);
    setAppointmentNextSlots([]);
  };

  const handleAppointmentSuccess = () => {
    handleCloseAppointmentDialog();
    showSuccess('Agendamento criado com sucesso!');
  };

  const handleSearchNextSlots = async (professionalId, serviceId, placeId) => {
    const key = `${professionalId}-${serviceId}-${placeId}`;
    setLoadingNextSlots(prev => ({ ...prev, [key]: true }));
    
    try {
      const slots = await appointmentsService.getNextAvailableSlots(professionalId, serviceId, placeId, 3) || [];
      
      if (slots.length === 0) {
        showError('Nenhum horário disponível nos próximos 30 dias');
        return;
      }
      
      // Abrir modal com os slots encontrados
      handleOpenAppointmentDialog(professionalId, serviceId, placeId, slots);
    } catch (err) {
      console.error('Erro ao buscar próximos horários:', err);
      showError('Erro ao buscar próximos horários disponíveis');
    } finally {
      setLoadingNextSlots(prev => ({ ...prev, [key]: false }));
    }
  };

  // ✅ Handlers de Foto
  const handlePhotoClick = (professional) => {
    setSelectedProfessional(professional);
    photoDialog.handleOpen();
  };

  const handlePhotoUpload = async (file) => {
    try {
      if (!selectedProfessional?.id) {
        showError('Profissional não selecionado');
        return;
      }
      await photoService.uploadProfilePhoto(file, selectedProfessional.id);
      
      // Salvar timestamp no localStorage para persistência
      const timestamp = Date.now();
      photoService.savePhotoTimestamp(selectedProfessional.id, timestamp);

      // Atualizar timestamp da foto no estado local para forçar reload da imagem (cache busting)
      setPhotoTimestampOverrides(prev => ({ ...prev, [selectedProfessional.id]: timestamp }));

      photoDialog.handleClose();
      showSuccess('Foto atualizada com sucesso!');

      // Recarregar profissionais após upload
      reload();
    } catch (err) {
      showError('Erro ao fazer upload: ' + (err.response?.data?.message || err.message));
    }
  };

  // ✅ Loading/Error States
  if (loading) return <LoadingState message="Carregando profissionais..." />;
  if (error) return <ErrorState error={error} onRetry={reload} />;
  if (!normalizedData) return null;

  return (
    <ProfessionalsDataProvider data={normalizedData}>
      <ProfessionalsPageContent
        professionals={normalizedData.professionals}
        places={normalizedData.places || []}
        services={normalizedData.services || []}
        hint={hint}
        formDialog={formDialog}
        deleteDialog={deleteDialog}
        photoDialog={photoDialog}
        selectedProfessional={selectedProfessional}
        snackbar={snackbar}
        showSuccess={showSuccess}
        hideSnackbar={hideSnackbar}
        reload={reload}
        handleSubmit={handleSubmit}
        handlePhotoClick={handlePhotoClick}
        handlePhotoUpload={handlePhotoUpload}
        handleOpenAppointmentDialog={handleOpenAppointmentDialog}
        handleSearchNextSlots={handleSearchNextSlots}
        loadingNextSlots={loadingNextSlots}
        getLabel={getLabel}
        getIcon={getIcon}
        onOpenWizard={() => setShowSmartWizard(true)}
        shareDialog={shareDialog}
        customPricesDialog={customPricesDialog}
      />
      
      {/* Modal de Agendamento - auto-suficiente, carrega próprios dados */}
      <AppointmentFormDialog
        open={appointmentDialogOpen}
        onClose={handleCloseAppointmentDialog}
        onSuccess={handleAppointmentSuccess}
        prefillConfig={appointmentPrefillConfig}
      />

      {/* SmartWizard */}
      <SmartWizard
        open={showSmartWizard}
        wizardId="add-professional"
        onClose={() => setShowSmartWizard(false)}
        onSuccess={() => {
          setShowSmartWizard(false);
          reload();
          showSuccess('Profissional criado com sucesso!');
        }}
      />
    </ProfessionalsDataProvider>
  );
}

// Componente interno que tem acesso ao context
function ProfessionalsPageContent({
  professionals,
  places,
  services,
  hint,
  formDialog,
  deleteDialog,
  photoDialog,
  selectedProfessional,
  snackbar,
  showSuccess,
  hideSnackbar,
  reload,
  handleSubmit,
  handlePhotoClick,
  handlePhotoUpload,
  handleOpenAppointmentDialog,
  handleSearchNextSlots,
  loadingNextSlots,
  getLabel,
  getIcon,
  onOpenWizard,
  shareDialog,
  customPricesDialog,
}) {
  const { getPlace, getService, getProfessionalStats } = useProfessionalsData();

  // Initialize queryClient for cache invalidation
  const queryClient = useQueryClient();

  const [searchText, setSearchText] = useState('');
  const [sortOrder, setSortOrder] = useState('name_asc');

  const filterAndSort = useCallback((list) => {
    let result = [...list];
    const q = searchText.trim().toLowerCase();
    if (q) {
      result = result.filter(p =>
        (p.name || '').toLowerCase().includes(q) ||
        (p.specialty || '').toLowerCase().includes(q) ||
        (p.email || '').toLowerCase().includes(q) ||
        (p.phone || '').toLowerCase().includes(q) ||
        (p.registrationNumber || '').toLowerCase().includes(q)
      );
    }
    result.sort((a, b) => {
      if (sortOrder === 'name_desc')
        return (b.name || '').localeCompare(a.name || '', 'pt-BR');
      if (sortOrder === 'specialty_asc')
        return (a.specialty || '').localeCompare(b.specialty || '', 'pt-BR');
      return (a.name || '').localeCompare(b.name || '', 'pt-BR'); // name_asc default
    });
    return result;
  }, [searchText, sortOrder]);

  // Separar profissionais owned vs shared
  const ownedProfessionals = filterAndSort(professionals.filter(p => !p.isShared));
  const sharedProfessionals = filterAndSort(professionals.filter(p => p.isShared));

  // Handle professional deletion with cache invalidation for cascaded data
  const handleProfessionalDelete = useCallback(() => {
    reload();
    queryClient.invalidateQueries({ queryKey: ['schedules'] });
    queryClient.invalidateQueries({ queryKey: ['places'] });
  }, [reload, queryClient]);

  // Helper para verificar permissão de profissional compartilhado
  const hasPermission = (professional, entityType, minLevel = 1) => {
    if (!professional.isShared) return true;
    return (professional.permissions?.[entityType] || 0) >= minLevel;
  };

  // Renderiza grid de profissionais (reutilizável para ambas seções)
  const renderProfessionalCards = (profList) => (
    <Box
      sx={{
        display: 'grid',
        gap: 2,
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, 1fr)',
          md: 'repeat(3, 1fr)',
          lg: 'repeat(3, 1fr)',
        },
        '@media (min-width: 1700px)': {
          gridTemplateColumns: 'repeat(4, 1fr)',
        },
      }}
    >
      {profList.map((professional) => {
        const stats = getProfessionalStats(professional);

        return (
          <Box key={professional.id} sx={{ minWidth: 0 }}>
            {renderProfessionalCard(professional, stats)}
          </Box>
        );
      })}
    </Box>
  );

  const renderProfessionalCard = (professional, stats) => {
    const shared = professional.isShared;
    const apptLevel = shared ? (professional.permissions?.[2] ?? 0) : 2;
    const canEdit = hasPermission(professional, 1, 2);
    const PlaceIconCmp = getIconClass(getIcon('places').name);
    const sharedTooltip = shared
      ? `Compartilhado${professional.ownerName ? ` por ${professional.ownerName}` : ''} · ${apptLevel >= 2 ? 'Edição' : 'Visualização'}`
      : '';

    return (
      <Card
        elevation={0}
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          p: 1.25,
          gap: 0.75,
          border: '1px solid',
          borderColor: 'divider',
          borderLeft: '3px solid',
          borderLeftColor: shared ? 'info.main' : 'primary.main',
          transition: 'box-shadow 0.2s, border-color 0.2s',
          '&:hover': {
            boxShadow: 2,
            borderColor: shared ? 'info.light' : 'primary.light',
            borderLeftColor: shared ? 'info.main' : 'primary.main',
          },
        }}
      >
        {/* Header: avatar + name + inline actions (fixo) */}
        <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'center', minWidth: 0, flexShrink: 0 }}>
          <Box sx={{ position: 'relative', flexShrink: 0 }}>
            <ProfessionalAvatar
              professionalId={professional.id}
              ownerUserId={professional.userId}
              photoTimestamp={professional.photoTimestamp}
              name={professional.name}
              sx={{
                width: 52,
                height: 52,
                bgcolor: shared ? 'info.main' : 'primary.main',
                fontSize: 18,
                fontWeight: 'bold',
              }}
            >
              {getInitials(professional.name)}
            </ProfessionalAvatar>
            {!shared && (
              <Tooltip title="Alterar foto">
                <IconButton
                  size="small"
                  onClick={() => handlePhotoClick(professional)}
                  sx={{
                    position: 'absolute',
                    bottom: -3,
                    right: -3,
                    width: 20,
                    height: 20,
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    '&:hover': { bgcolor: 'primary.main', color: 'common.white' },
                  }}
                >
                  <CameraIcon sx={{ fontSize: 12 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
              <Tooltip title={professional.name}>
                <Typography
                  variant="subtitle1"
                  fontWeight={700}
                  noWrap
                  sx={{ fontSize: '1rem', lineHeight: 1.25, minWidth: 0 }}
                >
                  {professional.name}
                </Typography>
              </Tooltip>
              {shared && (
                <Tooltip title={sharedTooltip}>
                  <ShareIcon sx={{ fontSize: 14, color: 'info.main', flexShrink: 0 }} />
                </Tooltip>
              )}
            </Box>
            {professional.specialty && (
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                sx={{ display: 'block', lineHeight: 1.3, fontSize: '0.75rem' }}
              >
                {professional.specialty}
              </Typography>
            )}
          </Box>

          <Box sx={{ display: 'flex', flexShrink: 0 }}>
            <Tooltip title="Agendar">
              <IconButton
                size="small"
                color="primary"
                onClick={() => handleOpenAppointmentDialog(professional.id)}
                sx={{ p: 0.5 }}
              >
                <ScheduleIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
            {!shared && (
              <Tooltip title="Compartilhar">
                <IconButton
                  size="small"
                  color="info"
                  onClick={() => shareDialog.handleOpen(professional)}
                  sx={{ p: 0.5 }}
                >
                  <ShareIcon sx={{ fontSize: 17 }} />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={shared && !canEdit ? 'Sem permissão de edição' : 'Editar'}>
              <span>
                <IconButton
                  size="small"
                  onClick={() => formDialog.handleOpen(professional)}
                  disabled={shared && !canEdit}
                  sx={{ p: 0.5 }}
                >
                  <EditIcon sx={{ fontSize: 17 }} />
                </IconButton>
              </span>
            </Tooltip>
            {!shared && (
              <Tooltip title="Excluir">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => deleteDialog.handleOpen(professional)}
                  sx={{ p: 0.5 }}
                >
                  <DeleteIcon sx={{ fontSize: 17 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* Stats + contact compact row */}
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <Chip
            icon={getIconComponent(getIcon('places').name)}
            label={`${stats.placesCount} ${stats.placesCount === 1 ? 'Local' : 'Locais'}`}
            size="small"
            variant="outlined"
            sx={{
              height: 20,
              fontSize: '0.68rem',
              '& .MuiChip-icon': { fontSize: 12, ml: '4px', mr: '-2px' },
              '& .MuiChip-label': { px: 0.75 },
            }}
          />
          <Chip
            icon={getIconComponent(getIcon('services').name)}
            label={`${stats.servicesCount} ${stats.servicesCount === 1 ? 'Serviço' : 'Serviços'}`}
            size="small"
            variant="outlined"
            color={stats.servicesCount > 0 ? 'primary' : 'default'}
            sx={{
              height: 20,
              fontSize: '0.68rem',
              '& .MuiChip-icon': { fontSize: 12, ml: '4px', mr: '-2px' },
              '& .MuiChip-label': { px: 0.75 },
            }}
          />
          {(professional.email || professional.phone || professional.registrationNumber) && (
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', ml: 'auto' }}>
              {professional.email && (
                <Tooltip title={professional.email}>
                  <EmailIcon sx={{ fontSize: 13, color: 'action.active', opacity: 0.7 }} />
                </Tooltip>
              )}
              {professional.phone && (
                <Tooltip title={professional.phone}>
                  <PhoneIcon sx={{ fontSize: 13, color: 'action.active', opacity: 0.7 }} />
                </Tooltip>
              )}
              {professional.registrationNumber && (
                <Tooltip title={professional.registrationNumber}>
                  <BadgeIcon sx={{ fontSize: 13, color: 'action.active', opacity: 0.7 }} />
                </Tooltip>
              )}
            </Box>
          )}
        </Box>

        {/* Services per place - área rolável */}
        <Box
          sx={{
            mt: 0.25,
            minWidth: 0,
            maxHeight: 220,
            overflowY: 'auto',
            overflowX: 'hidden',
            pr: 0.5,
            mr: -0.5,
            '&::-webkit-scrollbar': { width: 6 },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'action.disabledBackground',
              borderRadius: 3,
            },
            '&::-webkit-scrollbar-thumb:hover': {
              backgroundColor: 'action.disabled',
            },
          }}
        >
          {professional.places.length === 0 ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontStyle: 'italic', display: 'block', py: 0.5 }}
            >
              Nenhum serviço vinculado ainda
            </Typography>
          ) : (
            professional.places
              .filter((placeLink) => !!getPlace(placeLink.placeId))
              .map((placeLink, idx) => {
                const place = getPlace(placeLink.placeId);

                return (
                  <Box key={placeLink.id} sx={{ mt: idx > 0 ? 0.75 : 0 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        mb: 0.25,
                        minWidth: 0,
                      }}
                    >
                      <PlaceIconCmp sx={{ fontSize: 12, color: 'primary.main', flexShrink: 0 }} />
                      <Typography
                        variant="caption"
                        fontWeight={700}
                        noWrap
                        sx={{
                          letterSpacing: 0.3,
                          fontSize: '0.66rem',
                          color: 'text.primary',
                          minWidth: 0,
                        }}
                      >
                        {place.name}
                      </Typography>
                    </Box>

                    {placeLink.services.length === 0 ? (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontStyle: 'italic', pl: 2, display: 'block', fontSize: '0.68rem' }}
                      >
                        Nenhum serviço configurado
                      </Typography>
                    ) : (
                      placeLink.services
                        .filter((serviceLink) => !!getService(serviceLink.serviceId))
                        .map((serviceLink) => {
                          const service = getService(serviceLink.serviceId);
                          const slotKey = `${professional.id}-${serviceLink.serviceId}-${placeLink.placeId}`;
                          const isLoadingSlots = loadingNextSlots[slotKey];
                          const hasCustom = serviceLink.customPrices?.length > 0;

                          return (
                            <Box
                              key={serviceLink.id}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                pl: 1.75,
                                pr: 0.25,
                                py: 0.25,
                                minWidth: 0,
                                borderRadius: 0.5,
                                '&:hover': { bgcolor: 'action.hover' },
                              }}
                            >
                              <Typography
                                variant="body2"
                                noWrap
                                sx={{ flex: 1, minWidth: 0, fontSize: '0.78rem' }}
                              >
                                {service.name}
                              </Typography>
                              {service.durationMinutes != null && service.durationMinutes !== 0 && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ fontSize: '0.66rem', flexShrink: 0 }}
                                >
                                  {formatDuration(service.durationMinutes)}
                                </Typography>
                              )}
                              {service.price != null && service.price !== 0 && (
                                <Typography
                                  variant="caption"
                                  fontWeight={600}
                                  sx={{
                                    fontSize: '0.68rem',
                                    flexShrink: 0,
                                    color: hasCustom ? 'info.main' : 'success.main',
                                  }}
                                >
                                  {formatCurrency(service.price)}
                                  {hasCustom && '*'}
                                </Typography>
                              )}
                              <Tooltip title="Buscar próximos horários disponíveis">
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={isLoadingSlots}
                                    onClick={() => handleSearchNextSlots(
                                      professional.id,
                                      serviceLink.serviceId,
                                      placeLink.placeId
                                    )}
                                    sx={{ p: 0.25, flexShrink: 0 }}
                                  >
                                    {isLoadingSlots ? (
                                      <CircularProgress size={12} />
                                    ) : (
                                      <SearchIcon sx={{ fontSize: 14 }} />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                              {!shared && (
                                <Tooltip
                                  title={hasCustom
                                    ? `${serviceLink.customPrices.length} preço${serviceLink.customPrices.length > 1 ? 's' : ''} customizado${serviceLink.customPrices.length > 1 ? 's' : ''}`
                                    : 'Preços customizados'}
                                >
                                  <IconButton
                                    size="small"
                                    onClick={() => customPricesDialog.handleOpen({
                                      ...serviceLink,
                                      placeId: placeLink.placeId,
                                      placeName: place.name,
                                      professionalName: professional.name,
                                      serviceName: service.name,
                                    })}
                                    sx={{ p: 0.25, flexShrink: 0 }}
                                  >
                                    <PaymentIconMui
                                      sx={{ fontSize: 14 }}
                                      color={hasCustom ? 'success' : 'action'}
                                    />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          );
                        })
                    )}
                  </Box>
                );
              })
          )}
        </Box>
      </Card>
    );
  };

  return (
    <PageLayout
      themeKey="professionals"
      actionLabel="Novo Profissional"
      onAction={onOpenWizard}
      hint={hint}
      onHintAction={onOpenWizard}
    >
      {/* Empty State */}
      {professionals.length === 0 ? (
        <EmptyState
          icon={PersonIcon}
          title="Nenhum profissional cadastrado"
          message="Comece criando seu primeiro profissional para gerenciar serviços e agendamentos"
          action={
            <Button variant="contained" startIcon={<AddIcon />} onClick={onOpenWizard}>
              Criar Primeiro Profissional
            </Button>
          }
        />
      ) : (
        <Box>
          {/* Filter & Sort toolbar */}
          <PageFilters
            onClear={() => { setSearchText(''); setSortOrder('name_asc'); }}
            clearDisabled={!searchText && sortOrder === 'name_asc'}
          >
            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
              <TextField
                fullWidth
                placeholder="Buscar por nome, especialidade, e-mail..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: searchText ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchText('')} edge="end">
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Select
                fullWidth
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
              >
                <MenuItem value="name_asc">Nome A → Z</MenuItem>
                <MenuItem value="name_desc">Nome Z → A</MenuItem>
                <MenuItem value="specialty_asc">Especialidade A → Z</MenuItem>
              </Select>
            </Grid>
          </PageFilters>
          {/* No results */}
          {ownedProfessionals.length === 0 && sharedProfessionals.length === 0 && searchText.trim() && (
            <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
              <SearchIcon sx={{ fontSize: 40, mb: 1, opacity: 0.4 }} />
              <Typography variant="body1">
                Nenhum profissional encontrado para <strong>"{searchText}"</strong>
              </Typography>
            </Box>
          )}
          {/* Meus Profissionais */}
          {ownedProfessionals.length > 0 && (
            <Box mb={4}>
              <Typography variant="h6" fontWeight="bold" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
                Meus Profissionais ({ownedProfessionals.length})
              </Typography>
              {renderProfessionalCards(ownedProfessionals)}
            </Box>
          )}

          {/* Compartilhados comigo */}
          {sharedProfessionals.length > 0 && (
            <Box mb={4}>
              <Divider sx={{ mb: 3 }} />
              <Typography variant="h6" fontWeight="bold" color="info.main" gutterBottom sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <ShareIcon fontSize="small" />
                Compartilhados comigo ({sharedProfessionals.length})
              </Typography>
              {renderProfessionalCards(sharedProfessionals)}
            </Box>
          )}
        </Box>
      )}

      {/* Professional Form Dialog */}
      <ProfessionalForm
        open={formDialog.open}
        onClose={formDialog.handleClose}
        onSubmit={handleSubmit}
        data={formDialog.data}
      />

      {/* Delete Confirmation Dialog */}
      <CascadeDeleteDialog
        open={deleteDialog.open}
        onClose={deleteDialog.handleClose}
        onDeleted={() => {
          deleteDialog.handleClose();
          showSuccess('Excluído com sucesso!');
          handleProfessionalDelete();
        }}
        deletePath={`/professionals/${deleteDialog.data?.id}`}
        entityName={deleteDialog.data?.name}
      />

      {/* Share Professional Dialog */}
      <ShareProfessionalDialog
        open={shareDialog.open}
        onClose={shareDialog.handleClose}
        professional={shareDialog.data}
        onSuccess={() => {
          reload();
        }}
      />

      {/* Snackbar */}
      <AppSnackbar snackbar={snackbar} onClose={hideSnackbar} />

      {/* Profile Photo Upload Dialog */}
      <ProfilePhotoUpload
        open={photoDialog.open}
        onClose={photoDialog.handleClose}
        onUpload={handlePhotoUpload}
        currentPhotoUrl={selectedProfessional?.photoUrl}
      />

      {/* Professional Custom Prices Dialog */}
      <ProfessionalCustomPricesDialog
        open={customPricesDialog.open}
        onClose={customPricesDialog.handleClose}
        placeServiceLink={customPricesDialog.data}
        placeId={customPricesDialog.data?.placeId}
        onUpdate={reload}
      />

    </PageLayout>
  );
}
